import {
  appendNeuronBatchRequest,
  updateNeuronDebugState,
} from "./neuronDebugStore";

let boundFetch;

const parseJsonBody = (body) => {
  if (typeof body !== "string") {
    return null;
  }

  try {
    return JSON.parse(body);
  } catch (error) {
    return null;
  }
};

const extractSessionId = (payload) => {
  if (!payload) {
    return null;
  }

  const entry = Array.isArray(payload) ? payload[0] : payload;
  const sessionId = entry?.session_id ?? entry?.sessionId;
  return typeof sessionId === "string" ? sessionId : null;
};

const extractRequestId = (payload) => {
  if (!payload) {
    return null;
  }

  const entry = Array.isArray(payload) ? payload[0] : payload;
  const requestId = entry?.request_id ?? entry?.requestId;
  return typeof requestId === "string" ? requestId : null;
};

const extractEventSummaries = (payload) => {
  const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
  return arr.map((entry) => ({
    eventType: entry?.type ?? entry?.event_type ?? entry?.eventId ?? null,
    itemId: entry?.item_id ?? entry?.itemId ?? null,
    userId: entry?.user_id ?? entry?.userId ?? null,
    source: entry?.metadata?.source ?? entry?.source ?? null,
    timestamp: entry?.timestamp ?? entry?.time ?? null,
  }));
};

const getItemTitle = (item) => {
  const resolved = item?.item && typeof item.item === "object" ? item.item : item;
  const metadata = item?.metadata && typeof item.metadata === "object"
    ? item.metadata
    : resolved?.metadata && typeof resolved.metadata === "object"
    ? resolved.metadata
    : {};

  return (
    metadata.originalTitle ||
    metadata.title ||
    metadata.aiTitle ||
    resolved?.name ||
    resolved?.title ||
    resolved?.entity_id ||
    resolved?.id ||
    item?.item_id ||
    "Untitled item"
  );
};

const getRecommendationItems = (recommendations) =>
  recommendations.flatMap((recommendation) => {
    const group = (recommendation?.group || "").trim() || null;
    const items = Array.isArray(recommendation?.items)
      ? recommendation.items
      : [recommendation?.item && typeof recommendation.item === "object"
        ? recommendation.item
        : recommendation];

    return items.map((item) => {
      const metadata = item?.metadata && typeof item.metadata === "object"
        ? item.metadata
        : {};
      const score =
        typeof item?.score === "number"
          ? item.score
          : typeof recommendation?.score === "number"
          ? recommendation.score
          : null;
      // The similarity before damping. Cards show this as the match figure;
      // the debug panel shows both so the gap between them (impression
      // fatigue, rules) is visible rather than looking like a bug.
      const debug = item?._debug || recommendation?._debug || null;
      const baseScore =
        typeof debug?.base_score === "number" ? debug.base_score : null;

      return {
        id: item?.id ?? item?.item_id ?? recommendation?.item_id ?? item?.entity_id ?? null,
        baseScore,
        title: getItemTitle(item),
        description: metadata.summary || item?.description || null,
        source: metadata.source || item?.source || recommendation?.source || null,
        category: metadata.aiCategorization || group,
        score,
      };
    });
  });

const getRecommendationSnapshot = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const experiments = Array.isArray(data.experiments) ? data.experiments : [];
  const userSegments = Array.isArray(data.user_segments)
    ? data.user_segments
    : [];
  const recommendations = Array.isArray(data.recommendations)
    ? data.recommendations
    : Array.isArray(data.data)
    ? data.data
    : [];
  const returnedCount = recommendations.reduce((count, recommendation) => {
    if (Array.isArray(recommendation?.items)) {
      return count + recommendation.items.length;
    }

    return count + 1;
  }, 0);
  const embeddingInfo =
    data.embedding_info && typeof data.embedding_info === "object"
      ? {
          source: data.embedding_info.source ?? null,
          usedDefault: Boolean(data.embedding_info.used_default),
          defaultReason: data.embedding_info.default_reason ?? null,
          dimension:
            typeof data.embedding_info.dimension === "number"
              ? data.embedding_info.dimension
              : null,
          expectedDimension:
            typeof data.embedding_info.expected_dimension === "number"
              ? data.embedding_info.expected_dimension
              : null,
        }
      : null;

  return {
    updatedAt: new Date().toISOString(),
    message: data.message ?? null,
    requestId: data.request_id ?? null,
    quantity: typeof data.limit === "number" ? data.limit : typeof data.quantity === "number" ? data.quantity : null,
    returnedCount,
    items: getRecommendationItems(recommendations).slice(0, 16),
    processingTimeMs:
      typeof data.processing_time_ms === "number"
        ? data.processing_time_ms
        : null,
    pipelineId: data.pipeline_id ?? null,
    mode: data.mode ?? null,
    coldStartFallbackUsed: Boolean(data.cold_start_fallback_used),
    excludedViewedItems: data.excluded_viewed_items ?? null,
    embeddingInfo,
    userSegments,
    experiments: experiments.map((experiment) => ({
      experimentId: experiment.experiment_id ?? null,
      experimentName: experiment.experiment_name ?? null,
      variantId: experiment.variant_id ?? null,
      variantName: experiment.variant_name ?? null,
    })),
  };
};

const resolveRequestMetadata = (input, init) => {
  if (input && typeof input === "object" && "url" in input) {
    return {
      url: input.url,
      method: input.method || init?.method || "GET",
      body: init?.body,
    };
  }

  return {
    url: typeof input === "string" ? input : String(input),
    method: init?.method || "GET",
    body: init?.body,
  };
};

export function getBoundFetch() {
  if (boundFetch) {
    return boundFetch;
  }

  const hasWindowFetch =
    typeof window !== "undefined" && typeof window.fetch === "function";

  const fetchFn = hasWindowFetch
    ? window.fetch
    : typeof fetch === "function"
    ? fetch
    : undefined;

  if (!fetchFn) {
    return undefined;
  }

  const baseFetch = hasWindowFetch ? fetchFn.bind(window) : fetchFn;

  boundFetch = async (input, init) => {
    const {url, method, body} = resolveRequestMetadata(input, init);
    const upperMethod = method?.toUpperCase?.() || "GET";
    const urlString = typeof url === "string" ? url : "";
    const isEventRequest =
      urlString.includes("/events") && upperMethod === "POST";
    const isRecommendationsRequest = urlString.includes("/recommendations");

    if (isEventRequest) {
      const payload = parseJsonBody(body);
      const sessionId = extractSessionId(payload);
      const requestId = extractRequestId(payload);

      if (sessionId) {
        updateNeuronDebugState({sessionId});
      }

      if (requestId) {
        updateNeuronDebugState({requestId});
      }

      if (payload) {
        appendNeuronBatchRequest({
          timestamp: new Date().toISOString(),
          count: Array.isArray(payload) ? payload.length : 1,
          requestId: requestId || null,
          sessionId: sessionId || null,
          events: extractEventSummaries(payload),
        });
      }
    }

    const response = await baseFetch(input, init);

    if (isRecommendationsRequest) {
      try {
        const clone = response.clone();
        const text = await clone.text();
        const parsed = text ? JSON.parse(text) : null;
        // A batch answers for several rails; show the last one that served,
        // as the panel did when each rail made its own request.
        const data =
          parsed?.object === "recommendation_set_list"
            ? [...(parsed.results || [])].reverse().find((result) => result?.status < 400) ?? null
            : parsed;
        const requestId = data?.request_id;
        updateNeuronDebugState({
          ...(typeof requestId === "string" && requestId ? {requestId} : {}),
          latestRecommendation: getRecommendationSnapshot(data),
        });
      } catch (error) {
        // Ignore parsing errors for debug state updates.
      }
    }

    return response;
  };

  return boundFetch;
}
